import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TelnyxRTC } from "@telnyx/webrtc";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";

const SOFTPHONE_USERNAME_STORAGE_KEY = "vcrm_telnyx_softphone_username";
const SOFTPHONE_HEARTBEAT_MS = 15000;

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCallState(value) {
  switch (value) {
    case "ringing":
      return "Sonando";
    case "answering":
      return "Contestando";
    case "active":
      return "En llamada";
    case "held":
      return "En espera";
    case "hangup":
    case "destroy":
    case "destroyed":
      return "Finalizada";
    case "recovering":
    case "reconnecting":
      return "Reconectando";
    default:
      return value || "—";
  }
}

function statusColors(status, isDark) {
  if (status === "active") return { bg: isDark ? "rgba(34,197,94,0.18)" : "#dcfce7", color: "#15803d" };
  if (status === "ringing") return { bg: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7", color: "#b45309" };
  if (status === "error") return { bg: isDark ? "rgba(239,68,68,0.16)" : "#fee2e2", color: "#dc2626" };
  if (status === "ready") return { bg: isDark ? "rgba(59,130,246,0.18)" : "#dbeafe", color: "#2563eb" };
  if (status === "connecting") return { bg: isDark ? "rgba(168,85,247,0.18)" : "#ede9fe", color: "#7c3aed" };
  return { bg: isDark ? "rgba(148,163,184,0.16)" : "#f1f5f9", color: "#475569" };
}

function CopyField({ label, value, colors }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <code style={{ flex: 1, padding: "10px 12px", borderRadius: 10, background: colors.bgInput, border: `1px solid ${colors.border}`, color: colors.textPrimary, fontSize: 12, overflowWrap: "anywhere" }}>
          {value || "—"}
        </code>
        <button
          type="button"
          onClick={copy}
          style={{
            padding: "9px 12px",
            borderRadius: 10,
            border: `1px solid ${colors.border}`,
            background: copied ? colors.accentGreenLight : colors.bgCard,
            color: copied ? colors.accentGreen : colors.textSecondary,
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, children, colors }) {
  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 16, overflow: "hidden" }}>
      <div style={{ padding: "18px 20px", borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>{title}</div>
        {subtitle ? <div style={{ marginTop: 4, fontSize: 12.5, color: colors.textMuted }}>{subtitle}</div> : null}
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

function StatusBadge({ status, label, isDark }) {
  const tone = statusColors(status, isDark);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: tone.bg,
        color: tone.color,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {label}
    </span>
  );
}

export default function NumerosTelnyx() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const pollRef = useRef(null);
  const audioRef = useRef(null);
  const clientRef = useRef(null);
  const currentCallRef = useRef(null);
  const keepAliveRef = useRef(null);
  const registrationRef = useRef({ numero: "", sipUsername: "" });
  const selectedNumberRef = useRef("");
  const sipUsernameRef = useRef("");
  const lastCallDebugRef = useRef("");
  const callRecorderRef = useRef(null);
  const callRecordChunksRef = useRef([]);
  const callRecordMetaRef = useRef({ from: "", startedAt: "" });
  const callRecordStoppingRef = useRef(false);
  const callRecordFinalizedRef = useRef(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedNumber, setSelectedNumber] = useState("");
  const [sipUsername, setSipUsername] = useState(() => localStorage.getItem(SOFTPHONE_USERNAME_STORAGE_KEY) || "");
  const [sipPassword, setSipPassword] = useState("");
  const [softphoneStatus, setSoftphoneStatus] = useState("offline");
  const [softphoneMessage, setSoftphoneMessage] = useState("Desconectado");
  const [softphoneError, setSoftphoneError] = useState("");
  const [callInfo, setCallInfo] = useState(null);
  const [muted, setMuted] = useState(false);
  const [softphoneDebug, setSoftphoneDebug] = useState([]);
  const [localEvidence, setLocalEvidence] = useState(null);

  const pushSoftphoneDebug = useCallback((title, detail = "") => {
    const timestamp = new Date().toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    setSoftphoneDebug((prev) => [
      { id: `${Date.now()}-${Math.random()}`, timestamp, title, detail: String(detail || "") },
      ...prev,
    ].slice(0, 12));
  }, []);

  const stopLocalCallRecording = useCallback(({ autoDownload = true } = {}) => {
    const recorder = callRecorderRef.current;
    if (!recorder || callRecordStoppingRef.current || callRecordFinalizedRef.current) return;

    callRecordStoppingRef.current = true;
    callRecorderRef.current = null;

    const finish = async () => {
      if (callRecordFinalizedRef.current) return;
      callRecordFinalizedRef.current = true;

      const chunks = callRecordChunksRef.current.slice();
      callRecordChunksRef.current = [];
      if (!chunks.length) {
        pushSoftphoneDebug("Evidencia OTP", "Sin chunks de audio (grabación vacía)");
        return;
      }

      const mimeType = recorder.mimeType || chunks[0]?.type || "audio/webm";
      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size < 16 * 1024) {
        pushSoftphoneDebug(
          "Evidencia OTP",
          `Grabación demasiado pequeña (${blob.size} B) — se descarta (posible doble stop)`
        );
        return;
      }

      const ext = mimeType.includes("ogg") ? "ogg" : mimeType.includes("mp4") ? "m4a" : "webm";
      const started = callRecordMetaRef.current.startedAt || new Date().toISOString().replace(/[:.]/g, "-");
      const fromSafe = String(callRecordMetaRef.current.from || "meta").replace(/[^\d+a-zA-Z_-]/g, "");
      const filename = `wa-otp-${fromSafe || "call"}-${started}.${ext}`;
      const url = URL.createObjectURL(blob);

      setLocalEvidence((prev) => {
        if (prev?.url) {
          try { URL.revokeObjectURL(prev.url); } catch (_) {}
        }
        return {
          url,
          filename,
          bytes: blob.size,
          mimeType,
          createdAt: new Date().toISOString(),
          transcript: null,
          detectedCode: null,
          transcriptStatus: "pending",
          transcriptError: "",
        };
      });

      pushSoftphoneDebug("Evidencia OTP", `Grabación local lista (${Math.round(blob.size / 1024)} KB)`);

      if (autoDownload) {
        try {
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
        } catch (_) {}
      }

      try {
        pushSoftphoneDebug("Transcripción", "Enviando audio a Whisper…");
        const form = new FormData();
        form.append("audio", blob, filename);
        const res = await api.post("/telnyx/softphone/transcribe-evidence", form);
        const transcript = String(res.data?.transcript || "").trim();
        const detectedCode = String(res.data?.detected_code || "").trim();
        setLocalEvidence((prev) => (prev && prev.url === url ? {
          ...prev,
          transcript,
          detectedCode: detectedCode || null,
          transcriptStatus: "done",
          transcriptError: "",
        } : prev));
        pushSoftphoneDebug(
          "Transcripción lista",
          detectedCode ? `Código detectado: ${detectedCode}` : (transcript.slice(0, 120) || "(vacío)")
        );
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || "No se pudo transcribir";
        setLocalEvidence((prev) => (prev && prev.url === url ? {
          ...prev,
          transcriptStatus: "error",
          transcriptError: msg,
        } : prev));
        pushSoftphoneDebug("Error transcripción", msg);
      }
    };

    try {
      if (recorder.state !== "inactive") {
        try { recorder.requestData(); } catch (_) {}
        recorder.addEventListener("stop", () => { finish().catch(() => {}); }, { once: true });
        recorder.stop();
      } else {
        finish().catch(() => {});
      }
    } catch (e) {
      pushSoftphoneDebug("Error grabación local", e?.message || "No se pudo detener MediaRecorder");
      callRecordChunksRef.current = [];
      callRecordStoppingRef.current = false;
      callRecordFinalizedRef.current = false;
    }
  }, [pushSoftphoneDebug]);

  const startLocalCallRecording = useCallback((call) => {
    if (callRecorderRef.current || callRecordStoppingRef.current || !call?.remoteStream) return;
    const stream = call.remoteStream;
    if (!stream.getAudioTracks?.().length) return;
    if (typeof MediaRecorder === "undefined") {
      pushSoftphoneDebug("Grabación local", "MediaRecorder no disponible en este navegador");
      return;
    }

    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    const mimeType = candidates.find((t) => {
      try { return MediaRecorder.isTypeSupported?.(t); } catch (_) { return false; }
    }) || "";

    try {
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      callRecordChunksRef.current = [];
      callRecordStoppingRef.current = false;
      callRecordFinalizedRef.current = false;
      callRecordMetaRef.current = {
        from: call.remotePartyNumber || "",
        startedAt: new Date().toISOString().replace(/[:.]/g, "-"),
      };
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) callRecordChunksRef.current.push(ev.data);
      };
      recorder.onerror = (ev) => {
        pushSoftphoneDebug("Error grabación local", ev?.error?.message || "MediaRecorder error");
      };
      recorder.start(1000);
      callRecorderRef.current = recorder;
      pushSoftphoneDebug("Grabación local", "Grabando audio remoto (OTP Meta) para evidencia");
    } catch (e) {
      pushSoftphoneDebug("Error grabación local", e?.message || "No se pudo iniciar MediaRecorder");
    }
  }, [pushSoftphoneDebug]);

  const loadPanel = useCallback(async (numero = "", silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError("");
      const res = await api.get("/telnyx/panel", { params: numero ? { numero } : {} });
      const next = res.data?.data || null;
      setData(next);
      if (!numero && next?.selected_number) setSelectedNumber(next.selected_number);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo cargar Telnyx");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPanel("");
  }, [loadPanel]);

  useEffect(() => {
    selectedNumberRef.current = selectedNumber;
  }, [selectedNumber]);

  useEffect(() => {
    sipUsernameRef.current = sipUsername.trim();
    try {
      localStorage.setItem(SOFTPHONE_USERNAME_STORAGE_KEY, sipUsername.trim());
    } catch (_) {}
  }, [sipUsername]);

  useEffect(() => {
    if (!selectedNumber) return;
    loadPanel(selectedNumber, true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadPanel(selectedNumber, true), 8000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [selectedNumber, loadPanel]);

  const syncSoftphoneSession = useCallback(async ({ numero, sipUsername: username, online }) => {
    await api.post("/telnyx/softphone/session", {
      numero,
      sipUsername: username,
      online,
    });
  }, []);

  const stopKeepAlive = useCallback(() => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  }, []);

  const startKeepAlive = useCallback((numero, username) => {
    stopKeepAlive();
    keepAliveRef.current = setInterval(() => {
      syncSoftphoneSession({ numero, sipUsername: username, online: true }).catch(() => {});
    }, SOFTPHONE_HEARTBEAT_MS);
  }, [stopKeepAlive, syncSoftphoneSession]);

  const attachRemoteStream = useCallback((call) => {
    if (!audioRef.current || !call?.remoteStream) return;
    try {
      audioRef.current.srcObject = call.remoteStream;
      const playPromise = audioRef.current.play?.();
      if (playPromise?.catch) playPromise.catch(() => {});
      if (call.state === "active") startLocalCallRecording(call);
    } catch (_) {}
  }, [startLocalCallRecording]);

  const disconnectSoftphone = useCallback(async ({ message = "Desconectado", preserveError = false } = {}) => {
    stopKeepAlive();
    stopLocalCallRecording({ autoDownload: true });

    const registration = registrationRef.current;
    if (registration.numero && registration.sipUsername) {
      try {
        await syncSoftphoneSession({
          numero: registration.numero,
          sipUsername: registration.sipUsername,
          online: false,
        });
      } catch (_) {}
    }
    registrationRef.current = { numero: "", sipUsername: "" };

    if (currentCallRef.current) {
      try {
        currentCallRef.current.hangup();
      } catch (_) {}
      currentCallRef.current = null;
    }

    const client = clientRef.current;
    clientRef.current = null;
    if (client) {
      try {
        await Promise.resolve(client.disconnect());
      } catch (_) {}
      try { client.off("telnyx.ready"); } catch (_) {}
      try { client.off("telnyx.error"); } catch (_) {}
      try { client.off("telnyx.socket.error"); } catch (_) {}
      try { client.off("telnyx.socket.close"); } catch (_) {}
      try { client.off("telnyx.notification"); } catch (_) {}
    }

    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.srcObject = null;
      } catch (_) {}
    }

    setCallInfo(null);
    setMuted(false);
    setSoftphoneStatus("offline");
    setSoftphoneMessage(message);
    if (!preserveError) setSoftphoneError("");
  }, [stopKeepAlive, stopLocalCallRecording, syncSoftphoneSession]);

  useEffect(() => {
    return () => {
      disconnectSoftphone().catch(() => {});
    };
  }, [disconnectSoftphone]);

  const currentNumber = useMemo(() => {
    return (
      (data?.numbers || []).find((n) => n.phone_number === (selectedNumber || data?.selected_number)) ||
      null
    );
  }, [data, selectedNumber]);

  const messages = data?.messages || [];
  const calls = data?.calls || [];
  const serverSoftphoneSession = data?.softphone_session || null;
  const softphoneConnected = ["connecting", "ready", "ringing", "active"].includes(softphoneStatus);
  const activeDid = selectedNumber || data?.selected_number || "";

  const openRecording = useCallback(async (call) => {
    try {
      setError("");
      let url = call?.recording_public_url || call?.recording_url || "";
      if (call?.recording_id) {
        const res = await api.get(`/telnyx/recordings/${call.recording_id}/url`);
        url = res.data?.data?.url || url;
      }
      if (!url) {
        throw new Error("La grabación todavía no está disponible.");
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo abrir la grabación.");
    }
  }, []);

  const connectSoftphone = useCallback(async () => {
    const numero = selectedNumberRef.current || data?.selected_number || "";
    const username = sipUsernameRef.current;
    const password = sipPassword.trim();

    if (!numero) {
      setSoftphoneError("Selecciona primero el número Telnyx que quieres atender en el CRM.");
      return;
    }
    if (!username || !password) {
      setSoftphoneError("Introduce el usuario SIP y la contraseña de la credencial WebRTC de Telnyx.");
      return;
    }

    lastCallDebugRef.current = "";
    setSoftphoneDebug([]);
    setSoftphoneError("");
    setSoftphoneStatus("connecting");
    setSoftphoneMessage("Solicitando acceso al micrófono...");
    pushSoftphoneDebug("Conexión softphone", `Preparando ${numero || "sin DID"} con usuario ${username || "sin usuario"}`);

    try {
      const testStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      testStream.getTracks().forEach((track) => track.stop());
    } catch (e) {
      setSoftphoneStatus("error");
      setSoftphoneMessage("Sin acceso al micrófono");
      setSoftphoneError("El navegador no ha concedido acceso al micrófono.");
      return;
    }

    await disconnectSoftphone({ message: "Reconectando..." });

    const client = new TelnyxRTC({
      login: username,
      password,
      debug: false,
    });

    if (audioRef.current) {
      client.remoteElement = audioRef.current;
    }

    client.on("telnyx.ready", async () => {
      pushSoftphoneDebug("SDK listo", `DID ${numero}`);
      setSoftphoneStatus("ready");
      setSoftphoneMessage("Conectado. Esperando llamada.");
      try {
        await syncSoftphoneSession({ numero, sipUsername: username, online: true });
        registrationRef.current = { numero, sipUsername: username };
        startKeepAlive(numero, username);
        loadPanel(numero, true);
      } catch (e) {
        setSoftphoneStatus("error");
        setSoftphoneMessage("Sin registro backend");
        setSoftphoneError(e?.response?.data?.error || e.message || "No se pudo registrar el softphone en el backend.");
      }
    });

    client.on("telnyx.error", (sdkError) => {
      pushSoftphoneDebug("Error SDK", sdkError?.message || "Error Telnyx WebRTC");
      stopKeepAlive();
      setSoftphoneStatus("error");
      setSoftphoneMessage("Error Telnyx");
      setSoftphoneError(sdkError?.message || "Error conectando con Telnyx WebRTC.");
    });

    client.on("telnyx.socket.error", (socketError) => {
      pushSoftphoneDebug("Error socket", socketError?.message || socketError?.error || "Fallo de socket");
    });

    client.on("telnyx.socket.close", (closeEvent) => {
      pushSoftphoneDebug(
        "Socket cerrado",
        `code=${closeEvent?.code ?? "?"}${closeEvent?.reason ? ` reason=${closeEvent.reason}` : ""}`
      );
    });

    client.on("telnyx.notification", (notification) => {
      if (notification?.type !== "callUpdate" || !notification.call) return;
      const call = notification.call;
      currentCallRef.current = call;
      const debugKey = [
        call.id || call.callID || "",
        call.state || "",
        call.direction || "",
        call.remotePartyNumber || "",
      ].join("|");
      if (debugKey !== lastCallDebugRef.current) {
        lastCallDebugRef.current = debugKey;
        pushSoftphoneDebug(
          "Call update",
          `${call.direction || "?"} · ${call.state || "?"} · ${call.remotePartyNumber || "número oculto"}`
        );
      }

      setCallInfo({
        from: call.remotePartyNumber || "Número oculto",
        to: numero,
        state: call.state,
        direction: call.direction,
      });

      if (call.remoteStream) attachRemoteStream(call);

      if (call.state === "ringing" && call.direction === "inbound") {
        setSoftphoneStatus("ringing");
        setSoftphoneMessage(`Llamada entrante de ${call.remotePartyNumber || "número oculto"}`);
        return;
      }

      if (call.state === "answering") {
        setSoftphoneStatus("connecting");
        setSoftphoneMessage("Contestando llamada...");
        return;
      }

      if (call.state === "active") {
        setSoftphoneStatus("active");
        setSoftphoneMessage(`En llamada con ${call.remotePartyNumber || "número oculto"}`);
        attachRemoteStream(call);
        return;
      }

      if (["hangup", "destroy", "destroyed", "purge"].includes(call.state)) {
        stopLocalCallRecording({ autoDownload: true });
        currentCallRef.current = null;
        setMuted(false);
        if (audioRef.current) {
          try {
            audioRef.current.pause();
            audioRef.current.srcObject = null;
          } catch (_) {}
        }
        setSoftphoneStatus("ready");
        setSoftphoneMessage("Conectado. Esperando llamada.");
        loadPanel(numero, true);
      }
    });

    clientRef.current = client;
    setSoftphoneMessage("Conectando con Telnyx...");
    client.connect();
  }, [attachRemoteStream, data?.selected_number, disconnectSoftphone, loadPanel, pushSoftphoneDebug, sipPassword, startKeepAlive, stopKeepAlive, stopLocalCallRecording, syncSoftphoneSession]);

  const answerCall = useCallback(() => {
    try {
      currentCallRef.current?.answer();
      setSoftphoneMessage("Contestando llamada...");
      setSoftphoneStatus("connecting");
    } catch (e) {
      setSoftphoneStatus("error");
      setSoftphoneMessage("No se pudo contestar");
      setSoftphoneError(e?.message || "No se pudo contestar la llamada.");
    }
  }, []);

  const hangupCall = useCallback(() => {
    try {
      currentCallRef.current?.hangup();
      setSoftphoneMessage("Colgando...");
    } catch (e) {
      setSoftphoneError(e?.message || "No se pudo colgar la llamada.");
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!currentCallRef.current || softphoneStatus !== "active") return;
    try {
      if (muted) currentCallRef.current.unmuteAudio();
      else currentCallRef.current.muteAudio();
      setMuted((value) => !value);
    } catch (e) {
      setSoftphoneError(e?.message || "No se pudo cambiar el mute.");
    }
  }, [muted, softphoneStatus]);

  const actionButtonStyle = {
    padding: "10px 14px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.bgCard,
    color: c.textSecondary,
    cursor: "pointer",
    fontWeight: 700,
  };

  return (
    <div style={{ minHeight: "100vh", background: c.bgApp, padding: "28px 24px 40px" }}>
      <audio ref={audioRef} autoPlay playsInline />

      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: c.textPrimary }}>Numeros Telnyx</h1>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: c.textMuted, maxWidth: 860 }}>
              Panel para ver números Telnyx del CRM y, ahora, contestar llamadas desde el navegador. Para la verificación de WhatsApp con tus números españoles de Telnyx, la vía válida es la llamada de voz.
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadPanel(activeDid, true)}
            style={actionButtonStyle}
          >
            {refreshing ? "Actualizando..." : "Actualizar"}
          </button>
        </div>

        {error ? (
          <div style={{ padding: "12px 16px", borderRadius: 12, background: isDark ? "rgba(239,68,68,0.12)" : "#fef2f2", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}>
            {error}
          </div>
        ) : null}

        <SectionCard
          title="Número y webhooks"
          subtitle="El webhook sigue sirviendo para registrar llamadas del Call Control. Tus números españoles actuales son voice-only, así que WhatsApp debe verificarse por llamada."
          colors={c}
        >
          <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1fr) minmax(320px, 2fr)", gap: 18 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Número Telnyx</span>
              <select
                value={selectedNumber || data?.selected_number || ""}
                onChange={(e) => setSelectedNumber(e.target.value)}
                disabled={softphoneConnected}
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: `1px solid ${c.border}`,
                  background: c.bgInput,
                  color: c.textPrimary,
                  fontSize: 14,
                  opacity: softphoneConnected ? 0.7 : 1,
                }}
              >
                {(data?.numbers || []).length === 0 ? <option value="">Sin números</option> : null}
                {(data?.numbers || []).map((n) => (
                  <option key={n.phone_number} value={n.phone_number}>
                    {n.phone_number}{n.usuario_nombre ? ` · ${n.usuario_nombre}` : ""}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: c.textMuted }}>
                {currentNumber?.usuario_nombre ? `Asignado a ${currentNumber.usuario_nombre}` : "Sin usuario asignado en el CRM"}
              </div>
              {softphoneConnected ? (
                <div style={{ fontSize: 12, color: c.textMuted }}>
                  El número queda bloqueado mientras el softphone está conectado.
                </div>
              ) : null}
              {data?.telnyx_error ? (
                <div style={{ fontSize: 12, color: "#f59e0b" }}>
                  Telnyx API no respondió al listar números: {data.telnyx_error}
                </div>
              ) : null}
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              <CopyField label="Webhook Telnyx (voz)" value={data?.webhook_url || ""} colors={c} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                {[
                  ["Número principal", data?.main_number || "—"],
                  ["SMS recibidos", String(messages.length)],
                  ["Llamadas registradas", String(calls.length)],
                  ["Softphone backend", serverSoftphoneSession ? "Activo" : "Inactivo"],
                ].map(([label, value]) => (
                  <div key={label} style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${c.border}`, background: c.bgInput }}>
                    <div style={{ fontSize: 11, color: c.textMuted, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.04em" }}>{label}</div>
                    <div style={{ marginTop: 8, fontSize: 20, fontWeight: 800, color: c.textPrimary }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Softphone WebRTC"
          subtitle="Conecta aquí una credencial SIP/WebRTC de Telnyx. Cuando este panel está activo, el backend prioriza el navegador para ese DID antes que el desvío al móvil."
          colors={c}
        >
          <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 1.1fr) minmax(320px, 1fr)", gap: 18, alignItems: "start" }}>
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Usuario SIP / credencial Telnyx
                </label>
                <input
                  value={sipUsername}
                  onChange={(e) => setSipUsername(e.target.value)}
                  placeholder="gencred..."
                  autoComplete="username"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${c.border}`,
                    background: c.bgInput,
                    color: c.textPrimary,
                    fontSize: 14,
                  }}
                />
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Contraseña SIP
                </label>
                <input
                  type="password"
                  value={sipPassword}
                  onChange={(e) => setSipPassword(e.target.value)}
                  placeholder="Contraseña de la credencial"
                  autoComplete="current-password"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${c.border}`,
                    background: c.bgInput,
                    color: c.textPrimary,
                    fontSize: 14,
                  }}
                />
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                <StatusBadge
                  status={softphoneStatus}
                  label={softphoneMessage}
                  isDark={isDark}
                />
                {serverSoftphoneSession ? (
                  <span style={{ fontSize: 12, color: c.textMuted }}>
                    Backend activo para <code>{serverSoftphoneSession.sip_username}</code> desde {formatDateTime(serverSoftphoneSession.updated_at)}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: c.textMuted }}>
                    Sin navegador registrado para este DID en backend.
                  </span>
                )}
              </div>

              {softphoneError ? (
                <div style={{ padding: "12px 14px", borderRadius: 12, border: "1px solid rgba(239,68,68,0.25)", background: isDark ? "rgba(239,68,68,0.12)" : "#fef2f2", color: "#ef4444", fontSize: 13 }}>
                  {softphoneError}
                </div>
              ) : null}

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {!softphoneConnected ? (
                  <button
                    type="button"
                    onClick={connectSoftphone}
                    style={{ ...actionButtonStyle, background: c.accentBlue, color: "#fff", border: "none" }}
                  >
                    Conectar softphone
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => disconnectSoftphone()}
                    style={{ ...actionButtonStyle, background: isDark ? "rgba(239,68,68,0.14)" : "#fee2e2", color: "#dc2626", border: "1px solid rgba(239,68,68,0.25)" }}
                  >
                    Desconectar
                  </button>
                )}

                <button
                  type="button"
                  onClick={answerCall}
                  disabled={softphoneStatus !== "ringing"}
                  style={{
                    ...actionButtonStyle,
                    background: softphoneStatus === "ringing" ? c.accentGreen : c.bgCard,
                    color: softphoneStatus === "ringing" ? "#fff" : c.textMuted,
                    border: "none",
                    opacity: softphoneStatus === "ringing" ? 1 : 0.65,
                    cursor: softphoneStatus === "ringing" ? "pointer" : "not-allowed",
                  }}
                >
                  Contestar
                </button>

                <button
                  type="button"
                  onClick={hangupCall}
                  disabled={!["ringing", "connecting", "active"].includes(softphoneStatus)}
                  style={{
                    ...actionButtonStyle,
                    background: ["ringing", "connecting", "active"].includes(softphoneStatus) ? "#dc2626" : c.bgCard,
                    color: ["ringing", "connecting", "active"].includes(softphoneStatus) ? "#fff" : c.textMuted,
                    border: "none",
                    opacity: ["ringing", "connecting", "active"].includes(softphoneStatus) ? 1 : 0.65,
                    cursor: ["ringing", "connecting", "active"].includes(softphoneStatus) ? "pointer" : "not-allowed",
                  }}
                >
                  Colgar
                </button>

                <button
                  type="button"
                  onClick={toggleMute}
                  disabled={softphoneStatus !== "active"}
                  style={{
                    ...actionButtonStyle,
                    opacity: softphoneStatus === "active" ? 1 : 0.65,
                    cursor: softphoneStatus === "active" ? "pointer" : "not-allowed",
                  }}
                >
                  {muted ? "Quitar mute" : "Mute"}
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ padding: "16px 18px", borderRadius: 14, border: `1px solid ${c.border}`, background: c.bgInput }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Llamada actual
                </div>
                {callInfo ? (
                  <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: c.textPrimary }}>{callInfo.from}</div>
                    <div style={{ fontSize: 13, color: c.textMuted }}>
                      DID: {callInfo.to || "—"} · Estado: {formatCallState(callInfo.state)}
                    </div>
                    <div style={{ fontSize: 13, color: c.textMuted }}>
                      Dirección: {callInfo.direction || "—"}
                    </div>
                    {softphoneStatus === "active" ? (
                      <div style={{ fontSize: 13, color: c.accentGreen || "#16a34a", fontWeight: 700 }}>
                        Grabando audio remoto para evidencia OTP…
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ marginTop: 10, fontSize: 14, color: c.textMuted }}>
                    Cuando entre una llamada al DID seleccionado y el softphone esté conectado, te sonará aquí. El audio remoto se grabará automáticamente (útil si Meta corta el OTP).
                  </div>
                )}
                {localEvidence ? (
                  <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                    <div style={{ fontSize: 13, color: c.textSecondary }}>
                      Última evidencia: {localEvidence.filename} ({Math.round((localEvidence.bytes || 0) / 1024)} KB)
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <a
                        href={localEvidence.url}
                        download={localEvidence.filename}
                        style={{ ...actionButtonStyle, textDecoration: "none", display: "inline-block" }}
                      >
                        Descargar audio
                      </a>
                      <audio controls src={localEvidence.url} style={{ width: "100%", maxWidth: 360 }} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Transcripción (Whisper)
                    </div>
                    {localEvidence.transcriptStatus === "pending" ? (
                      <div style={{ fontSize: 13, color: c.textMuted }}>Transcribiendo…</div>
                    ) : null}
                    {localEvidence.transcriptStatus === "error" ? (
                      <div style={{ fontSize: 13, color: "#ef4444" }}>{localEvidence.transcriptError || "Error al transcribir"}</div>
                    ) : null}
                    {localEvidence.detectedCode ? (
                      <div style={{ fontSize: 18, fontWeight: 800, color: c.accentGreen || "#16a34a", letterSpacing: "0.08em" }}>
                        Código detectado: {localEvidence.detectedCode}
                      </div>
                    ) : null}
                    {localEvidence.transcript ? (
                      <div style={{ fontSize: 14, color: c.textSecondary, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
                        {localEvidence.transcript}
                      </div>
                    ) : localEvidence.transcriptStatus === "done" ? (
                      <div style={{ fontSize: 13, color: c.textMuted }}>(sin texto reconocido)</div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div style={{ padding: "16px 18px", borderRadius: 14, border: `1px solid ${c.border}`, background: c.bgInput }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Diagnostico softphone
                </div>
                {softphoneDebug.length === 0 ? (
                  <div style={{ marginTop: 10, fontSize: 14, color: c.textMuted }}>
                    Aqui apareceran los eventos del navegador si la llamada se corta.
                  </div>
                ) : (
                  <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                    {softphoneDebug.map((item) => (
                      <div key={item.id} style={{ fontSize: 12, color: c.textSecondary, borderBottom: `1px solid ${c.border}`, paddingBottom: 8 }}>
                        <div style={{ fontWeight: 700, color: c.textPrimary }}>
                          {item.timestamp} · {item.title}
                        </div>
                        {item.detail ? <div style={{ marginTop: 2, whiteSpace: "pre-wrap" }}>{item.detail}</div> : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ padding: "16px 18px", borderRadius: 14, border: `1px solid ${c.border}`, background: c.bgInput }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Pasos exactos en Telnyx
                </div>
                <ol style={{ margin: "12px 0 0", paddingLeft: 18, color: c.textSecondary, fontSize: 14, lineHeight: 1.55 }}>
                  <li>Crea un <code>Credential Connection</code> en Telnyx Mission Control para el navegador.</li>
                  <li>Dentro de esa conexión, crea una credencial y copia <code>username</code> y <code>password</code>.</li>
                  <li>No cambies tu número del <code>Call Control App</code> actual; esta pantalla usa esa llamada entrante y la deriva al navegador.</li>
                  <li>Pega esa credencial aquí y pulsa <code>Conectar softphone</code>.</li>
                  <li>En WhatsApp Embedded Signup usa uno de tus números <code>+34...</code> y elige <code>Voice call</code>.</li>
                  <li>Cuando entre la llamada, pulsa <code>Contestar</code> y escucha el código en el navegador.</li>
                </ol>
              </div>
            </div>
          </div>
        </SectionCard>

        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 18, alignItems: "start" }}>
          <SectionCard title="Mensajes entrantes" subtitle="El CRM puede guardar SMS si Telnyx los envía al webhook, pero tus números españoles actuales son voice-only para la verificación de WhatsApp." colors={c}>
            {loading && !data ? (
              <div style={{ color: c.textMuted }}>Cargando...</div>
            ) : messages.length === 0 ? (
              <div style={{ color: c.textMuted }}>No hay SMS/MMS entrantes guardados para este número todavía.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {messages.map((m) => (
                  <div key={m.id} style={{ border: `1px solid ${c.border}`, borderRadius: 12, padding: "14px 16px", background: c.bgInput }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>{m.from_number || "Remitente desconocido"}</div>
                      <div style={{ fontSize: 12, color: c.textMuted }}>{formatDateTime(m.received_at || m.created_at)}</div>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 14, color: c.textSecondary, whiteSpace: "pre-wrap" }}>
                      {m.text || "(MMS o mensaje sin texto)"}
                    </div>
                    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: c.textMuted }}>A: {m.to_number || "—"}</span>
                      <span style={{ fontSize: 11, color: c.textMuted }}>Tipo: {m.message_type || "SMS"}</span>
                      <span style={{ fontSize: 11, color: c.textMuted }}>Estado webhook: {m.status || "—"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Llamadas entrantes" subtitle="Se registran al entrar al DID Telnyx. Si el softphone está activo, el backend transfiere la llamada al navegador antes que al móvil y ahora intenta guardar grabación y transcripción." colors={c}>
            {loading && !data ? (
              <div style={{ color: c.textMuted }}>Cargando...</div>
            ) : calls.length === 0 ? (
              <div style={{ color: c.textMuted }}>No hay llamadas entrantes registradas para este número todavía.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {calls.map((call) => {
                  const transcriptText = call.recording_transcription_text || call.transcripcion || "";
                  const hasRecording = Boolean(call.recording_id || call.recording_public_url || call.recording_url);
                  const detectedCode = call.recording_detected_code || "";
                  const transcriptProvider = call.recording_transcription_provider || (call.recording_transcription_text ? "telnyx" : "");
                  const transcriptionPending =
                    call.recording_status === "saved" &&
                    !transcriptText &&
                    call.recording_transcription_status !== "error";

                  return (
                    <div key={call.id} style={{ border: `1px solid ${c.border}`, borderRadius: 12, padding: "14px 16px", background: c.bgInput }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>
                          {call.from_number || "Origen desconocido"} → {call.to_number || "—"}
                        </div>
                        <div style={{ fontSize: 12, color: c.textMuted }}>{formatDateTime(call.started_at || call.created_at)}</div>
                      </div>
                      <div style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap", fontSize: 12, color: c.textMuted }}>
                        <span>Estado: {call.estado || "—"}</span>
                        <span>Agente: {call.agente_nombre || "Sin asignar"}</span>
                        <span>Duración: {call.duration_seconds != null ? `${call.duration_seconds}s` : "—"}</span>
                        <span>Grabación: {call.recording_status || "—"}</span>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: c.textMuted }}>
                        Contestada: {formatDateTime(call.answered_at)} · Finalizada: {formatDateTime(call.ended_at)}
                      </div>
                      {call.hangup_cause || call.hangup_source || call.sip_hangup_cause ? (
                        <div style={{ marginTop: 8, fontSize: 12, color: c.textMuted }}>
                          Fin: {call.hangup_cause || "—"} · Quién cuelga: {call.hangup_source || "—"} · SIP: {call.sip_hangup_cause || "—"}
                        </div>
                      ) : null}
                      {call.recording_error ? (
                        <div style={{ marginTop: 10, fontSize: 12, color: "#dc2626" }}>
                          Error de grabación: {call.recording_error}
                        </div>
                      ) : null}
                      {transcriptionPending ? (
                        <div style={{ marginTop: 10, fontSize: 12, color: c.textMuted }}>
                          Transcripción procesándose...
                        </div>
                      ) : null}
                      {detectedCode ? (
                        <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, border: `1px solid ${c.border}`, background: isDark ? "rgba(34,197,94,0.12)" : "#f0fdf4" }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            Posible código detectado
                          </div>
                          <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800, color: c.textPrimary, letterSpacing: "0.08em" }}>
                            {detectedCode}
                          </div>
                        </div>
                      ) : null}
                      {transcriptText ? (
                        <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.bgCard }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            Transcripción{transcriptProvider ? ` · ${transcriptProvider}` : ""}
                          </div>
                          <div style={{ marginTop: 6, fontSize: 13, color: c.textSecondary, whiteSpace: "pre-wrap" }}>
                            {transcriptText}
                          </div>
                        </div>
                      ) : null}
                      {hasRecording ? (
                        <div style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            onClick={() => openRecording(call)}
                            style={actionButtonStyle}
                          >
                            Abrir grabación
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
